/**
 * PKCE mechanics against Keycloak — G3.2, `docs/adr/0043`.
 *
 * Pure functions only: no React, no module-level session state, no `fetch` retried or
 * cached. `./session.ts` is the one place that turns these into a session; this file's
 * only job is "talk to the token endpoint correctly," so it can be read (and tested)
 * without also reading how login/logout/refresh are orchestrated.
 *
 * Keycloak's public client `bata-ui` in the `bata` realm requires PKCE with S256 — a
 * public client has no client secret to prove it originated the authorization request,
 * so the code-challenge/code-verifier pair stands in for one (RFC 7636). That is why
 * there is no client secret anywhere in this module: a public SPA client cannot keep one,
 * and Keycloak is configured not to expect it.
 *
 * No PKCE/OIDC library is added for this — `crypto.subtle.digest('SHA-256', …)` plus
 * `fetch` and `URLSearchParams` cover the whole surface, consistent with
 * `newIdempotencyKey()` in `src/api/http.ts` reaching for `globalThis.crypto` directly
 * rather than pulling in a UUID package.
 */

/**
 * Issuer base and client id, overridable the way `DEFAULT_BASE_URL` is in
 * `src/api/http.ts`: a hardcoded production default so the build works with no env
 * config, and a `VITE_*` override so local/dev/CI point at a different realm without a
 * code change.
 */
const DEFAULT_ISSUER = 'https://planning.bataoptima.com/realms/bata';
const DEFAULT_CLIENT_ID = 'bata-ui';

export const OIDC_ISSUER: string = import.meta.env['VITE_OIDC_ISSUER'] ?? DEFAULT_ISSUER;
export const OIDC_CLIENT_ID: string = import.meta.env['VITE_OIDC_CLIENT_ID'] ?? DEFAULT_CLIENT_ID;

const AUTHORIZE_ENDPOINT = `${OIDC_ISSUER}/protocol/openid-connect/auth`;
const TOKEN_ENDPOINT = `${OIDC_ISSUER}/protocol/openid-connect/token`;
const END_SESSION_ENDPOINT = `${OIDC_ISSUER}/protocol/openid-connect/logout`;

// ── errors ───────────────────────────────────────────────────────────────────
//
// Mirrors `src/api/problem.ts`'s split between "the transport never got a response" and
// "the response came back but was not what the contract promises" — an auth failure is
// exactly as reportable as an API failure, so it gets the same two shapes rather than a
// bare thrown string somewhere in a login button's click handler.

/** The token endpoint could not be reached at all — no status, no body. */
export class OidcUnreachableError extends Error {
  override readonly cause: unknown;

  constructor(cause: unknown) {
    super('The Keycloak token endpoint could not be reached.');
    this.name = 'OidcUnreachableError';
    this.cause = cause;
  }
}

/** The token endpoint returned a non-2xx status. Keycloak's error body is `{error,
 *  error_description}` per RFC 6749 §5.2, kept as `body` rather than parsed further —
 *  this module has no catalogue key to map it to, unlike `problemMessage` in
 *  `src/api/problem.ts`; that mapping is a UI concern for whoever renders a login
 *  failure, not a mechanics concern for whoever exchanges a code. */
export class OidcTokenError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown) {
    super(`Keycloak token endpoint returned ${status}.`);
    this.name = 'OidcTokenError';
    this.status = status;
    this.body = body;
  }
}

/** The token endpoint returned 2xx but the body is missing a field this flow requires
 *  (e.g. no `refresh_token` on an authorization_code exchange). A 2xx with a shape the
 *  contract doesn't honour is a defect to surface, not a value to limp forward with. */
export class OidcMalformedTokenResponseError extends Error {
  readonly body: unknown;

  constructor(body: unknown) {
    super('Keycloak token endpoint returned 200 with an unusable token response.');
    this.name = 'OidcMalformedTokenResponseError';
    this.body = body;
  }
}

// ── PKCE pair ────────────────────────────────────────────────────────────────

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  // btoa/atob operate on the browser's global scope; this runs only in a browser tab
  // (login/callback are both window-driven), so there is no Node fallback to write.
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** A fresh PKCE code verifier — 32 random bytes, base64url-encoded (43 chars), well
 *  inside RFC 7636's 43-128 char requirement. */
export function generateCodeVerifier(): string {
  const bytes = new Uint8Array(32);
  globalThis.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** `S256` code challenge for a verifier: base64url(SHA-256(verifier)), per RFC 7636 §4.2. */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  );
  return base64UrlEncode(new Uint8Array(digest));
}

/** The full Keycloak authorize-endpoint URL for a login redirect. `scope` is `openid
 *  profile` — `openid` because this is OIDC and not bare OAuth (we need an ID token to
 *  read an actor from), `profile` because that's what carries `preferred_username`.
 *  `kcAction` turns the same request into an application-initiated action — see
 *  `KeycloakAction` below. */
export function buildAuthorizeUrl(
  state: string,
  codeChallenge: string,
  redirectUri: string,
  kcAction?: KeycloakAction,
): string {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: OIDC_CLIENT_ID,
    redirect_uri: redirectUri,
    scope: 'openid profile',
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });
  if (kcAction !== undefined) params.set('kc_action', kcAction);
  return `${AUTHORIZE_ENDPOINT}?${params.toString()}`;
}

/**
 * A Keycloak application-initiated action (AIA): an ordinary authorization request that also
 * carries `kc_action=<required action alias>`. Keycloak runs that required action for the
 * signed-in user — for `UPDATE_PASSWORD`, its own "update password" page, re-asking for the
 * current password first when the sign-in is older than the action's max age — and then
 * finishes the authorization exactly as a login does, redirecting back to `redirect_uri` with a
 * `code` plus `kc_action_status` (`success`, `cancelled` or `error`).
 *
 * So a password change is a login round trip with one extra parameter, and the password never
 * passes through this application: no form here, no endpoint on the platform, nothing to keep.
 * It requires the alias to be ENABLED among the realm's required actions — `UPDATE_PASSWORD`
 * is, in `bata-infra`'s `stacks/keycloak/realms/bata-realm.json` (Keycloak 26.7), and has to
 * be, since the realm's `forceExpiredPasswordChange(180)` policy runs the same action.
 *
 * A closed union rather than `string`: an alias the realm does not enable makes Keycloak answer
 * the redirect with an error, and the set this application starts is a decision, not an input.
 */
export type KeycloakAction = 'UPDATE_PASSWORD' | 'UPDATE_PROFILE' | 'UPDATE_EMAIL';

const KEYCLOAK_ACTIONS: readonly KeycloakAction[] = ['UPDATE_PASSWORD', 'UPDATE_PROFILE', 'UPDATE_EMAIL'];

/** A stored action name back as the closed union, or `null` for anything else. */
export function asKeycloakAction(value: string | null): KeycloakAction | null {
  return KEYCLOAK_ACTIONS.find((action) => action === value) ?? null;
}

/** What Keycloak reports about an application-initiated action on the callback URL. */
export type KeycloakActionStatus = 'success' | 'cancelled' | 'error';

/**
 * `kc_action_status` off a callback's query string, or `null` on an ordinary sign-in. A pure
 * parse of a string, not of `window`, like the scope parses (CLAUDE.md §3.4). A value Keycloak
 * does not document is `null` too: this reports what Keycloak said, never a guess at it.
 */
export function readKeycloakActionStatus(search: string): KeycloakActionStatus | null {
  const status = new URLSearchParams(search).get('kc_action_status');
  return status === 'success' || status === 'cancelled' || status === 'error' ? status : null;
}

/** RP-initiated logout URL (Keycloak's `/protocol/openid-connect/logout`). `idToken` is
 *  `null` when this tab never held one (e.g. it restored a session purely from a
 *  persisted refresh token — see `./session.ts` — and hasn't re-exchanged since); Keycloak
 *  accepts an end-session request identified by `client_id` alone in that case, so the
 *  hint is included only when there is one rather than blocking logout on its absence. */
export function buildEndSessionUrl(idToken: string | null, postLogoutRedirectUri: string): string {
  const params = new URLSearchParams({ post_logout_redirect_uri: postLogoutRedirectUri });
  if (idToken) params.set('id_token_hint', idToken);
  else params.set('client_id', OIDC_CLIENT_ID);
  return `${END_SESSION_ENDPOINT}?${params.toString()}`;
}

// ── token endpoint ───────────────────────────────────────────────────────────

interface TokenResponseBody {
  readonly access_token?: string;
  readonly refresh_token?: string;
  readonly id_token?: string;
  readonly expires_in?: number;
}

async function postToken(body: URLSearchParams): Promise<TokenResponseBody> {
  let response: Response;
  try {
    response = await fetch(TOKEN_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
  } catch (cause) {
    throw new OidcUnreachableError(cause);
  }

  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    parsed = undefined;
  }

  if (!response.ok) throw new OidcTokenError(response.status, parsed);
  return (parsed ?? {}) as TokenResponseBody;
}

/** Authorization-code exchange, the last step of the redirect login flow. */
export async function exchangeCodeForTokens(
  code: string,
  codeVerifier: string,
  redirectUri: string,
): Promise<{ accessToken: string; refreshToken: string; idToken: string; expiresIn: number }> {
  const token = await postToken(new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: OIDC_CLIENT_ID,
    code,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
  }));

  if (!token.access_token || !token.refresh_token || !token.id_token || token.expires_in === undefined) {
    throw new OidcMalformedTokenResponseError(token);
  }
  return {
    accessToken: token.access_token,
    refreshToken: token.refresh_token,
    idToken: token.id_token,
    expiresIn: token.expires_in,
  };
}

/** Silent refresh. Keycloak's `bata` realm returns a rotated `refresh_token` on every
 *  refresh grant (refresh token rotation), so the caller must persist the NEW one and
 *  not reuse the one it called with — reusing a rotated-out token is a revoked-token
 *  error on the next attempt. */
export async function refreshTokens(
  refreshToken: string,
): Promise<{ accessToken: string; refreshToken: string; expiresIn: number }> {
  const token = await postToken(new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: OIDC_CLIENT_ID,
    refresh_token: refreshToken,
  }));

  if (!token.access_token || !token.refresh_token || token.expires_in === undefined) {
    throw new OidcMalformedTokenResponseError(token);
  }
  return {
    accessToken: token.access_token,
    refreshToken: token.refresh_token,
    expiresIn: token.expires_in,
  };
}
